import { useCallback, useEffect, useRef, useState } from "react";
import { getPersonalWork, type PersonalWorkPage, type PersonalWorkIssue } from "../../../lib/brain-api";
/** Follow Jira cursors, publishing progressive snapshots; stop on obsolete loads. */
export async function loadPersonalWork(
    onPage: (page: PersonalWorkPage) => void,
    isCurrent: () => boolean,
): Promise<boolean> {
    const rows = new Map<string, PersonalWorkIssue>(), cursors = new Set<string>();
    let cursor: string | undefined, first: PersonalWorkPage | null = null;
    do {
        const page = await getPersonalWork(cursor);
        if (!isCurrent()) return false;
        first ??= page;
        page.issues.forEach(i => rows.set(i.key, i));
        onPage({ ...first, issues: [...rows.values()], nextPageToken: page.nextPageToken });
        cursor = page.nextPageToken ?? undefined;
        if (cursor && cursors.has(cursor)) throw new Error("Jira repeated a page cursor. Some work is still missing; refresh to retry.");
        if (cursor) cursors.add(cursor);
    } while (cursor);
    return true;
}

export function usePersonalWork(userId?: string) {
    const [work, setWork] = useState<PersonalWorkPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const generation = useRef(0);
    const reload = useCallback(async () => {
        const run = ++generation.current;
        setWork(null);
        setComplete(false);
        setError(null);
        if (!userId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const finished = await loadPersonalWork(setWork, () => generation.current === run);
            if (finished && generation.current === run) setComplete(true);
        }
        catch (e) {
            if (generation.current === run)
                setError(e instanceof Error ? e.message : "Couldn't load your Jira work.");
        }
        finally {
            if (generation.current === run)
                setLoading(false);
        }
    }, [userId]);
    useEffect(() => { void reload(); return () => { generation.current++; }; }, [reload]);
    return { work, loading, complete, error, reload };
}
